import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectButton } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { firstValueFrom } from 'rxjs';

import { PageHeader } from '@core/components/page-header/page-header';
import { ImageFallbackDirective } from '@core/directives/image-fallback';
import { ServiceResponseV1 } from '@core/models/apis';
import { EntreeV1, MenuItemPendingQuery, MenuItemStatus } from '@core/models/menu-item-pending';
import { ProducerV1 } from '@core/models/producer-admin';
import { MenuItemsService } from '@core/services/menu-items';
import { ProducersService } from '@core/services/producers';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT = 'submittedAt:asc';
const ALL_STATUSES = 'All';

interface StatusTab {
  label: string;
  value: string;
}

@Component({
  selector: 'app-menu-items-pending-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    AutoComplete,
    TableModule,
    ButtonModule,
    IconField,
    InputIcon,
    InputTextModule,
    SelectButton,
    TagModule,
    MessageModule,
    SkeletonModule,
    PageHeader,
    ImageFallbackDirective
  ],
  templateUrl: './menu-items-pending-list.html',
  styleUrl: './menu-items-pending-list.scss'
})
export class MenuItemsPendingList {
  private readonly menuItems = inject(MenuItemsService);
  private readonly producers = inject(ProducersService);
  private readonly router = inject(Router);

  readonly page = signal<ServiceResponseV1<EntreeV1[]> | null>(null);
  readonly loading = signal<boolean>(false);
  readonly error = signal<boolean>(false);

  // Draft filter inputs; committed into the query only via applyFilters().
  readonly searchText = signal<string>('');
  readonly producerFilter = signal<ProducerV1 | null>(null);
  readonly producerSuggestions = signal<ProducerV1[]>([]);

  // Committed query state - the load effect reads only these.
  private readonly appliedSearch = signal<string>('');
  private readonly appliedProducerId = signal<string>('');
  readonly status = signal<MenuItemStatus | null>(MenuItemStatus.PendingApproval);
  readonly pageNum = signal<number>(1);
  readonly pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  readonly sort = signal<string>(DEFAULT_SORT);

  readonly statusCounts = signal<Record<string, number>>({});
  private readonly unresolvedProducerIds = signal<ReadonlySet<string>>(new Set<string>());

  readonly rows = computed<EntreeV1[]>(() => this.page()?.data ?? []);
  readonly totalRecords = computed<number>(() => this.page()?.paging?.totalSize ?? 0);
  readonly showStatusColumn = computed<boolean>(() => this.status() === null);
  readonly producerNameById = computed<Record<string, string>>(() => this.producers.producerNameById());
  readonly statusTabValue = computed<string>(() => this.status() ?? ALL_STATUSES);
  readonly statusTabs = computed<StatusTab[]>(() => {
    const counts = this.statusCounts();
    const label = (name: string, status: string): string =>
      counts[status] !== undefined ? `${name} (${counts[status]})` : name;
    return [
      { label: label('Pending', MenuItemStatus.PendingApproval), value: MenuItemStatus.PendingApproval },
      { label: label('Rejected', MenuItemStatus.Rejected), value: MenuItemStatus.Rejected },
      { label: label('Completed', MenuItemStatus.Completed), value: MenuItemStatus.Completed },
      { label: ALL_STATUSES, value: ALL_STATUSES }
    ];
  });
  readonly emptyMessage = computed<string>(() =>
    this.status() === MenuItemStatus.PendingApproval
      ? 'No menu items pending approval.'
      : 'No menu items match the current filters.'
  );

  constructor() {
    effect(() => {
      const query: MenuItemPendingQuery = {
        pageNum: this.pageNum(),
        pageSize: this.pageSize(),
        sort: this.sort(),
        status: this.status(),
        producerId: this.appliedProducerId() || undefined,
        search: this.appliedSearch() || undefined
      };
      untracked(() => void this.runLoad(query));
    });
    void this.loadStatusCounts();
  }

  applyFilters(): void {
    this.appliedSearch.set(this.searchText().trim());
    this.appliedProducerId.set(this.producerFilter()?.id ?? '');
    this.pageNum.set(1);
  }

  clearFilters(): void {
    this.searchText.set('');
    this.producerFilter.set(null);
    this.appliedSearch.set('');
    this.appliedProducerId.set('');
    this.pageNum.set(1);
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText().trim() || this.producerFilter() || this.appliedSearch() || this.appliedProducerId());
  }

  setStatus(value: string): void {
    this.status.set(value === ALL_STATUSES ? null : (value as MenuItemStatus));
    this.pageNum.set(1);
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? DEFAULT_PAGE_SIZE;
    this.pageSize.set(rows);
    this.pageNum.set(Math.floor((event.first ?? 0) / rows) + 1);
    if (typeof event.sortField === 'string' && event.sortField.length > 0) {
      this.sort.set(`${event.sortField}:${(event.sortOrder ?? 1) === 1 ? 'asc' : 'desc'}`);
    }
  }

  async searchProducers(event: AutoCompleteCompleteEvent): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.producers.getProducersList({ pageNum: 1, pageSize: 10, search: event.query })
      );
      this.producerSuggestions.set(response.data ?? []);
    } catch {
      this.producerSuggestions.set([]);
    }
  }

  resolveProducerName(item: EntreeV1): string {
    const name = this.producerNameById()[item.producerId];
    if (name) {
      return name;
    }
    return this.unresolvedProducerIds().has(item.producerId) ? '\u2014' : '\u2026';
  }

  showMarketingName(item: EntreeV1): boolean {
    return !!item.marketingName && item.marketingName.trim() !== item.name.trim();
  }

  formatAge(submittedAt: string | null | undefined): string {
    if (!submittedAt) {
      return '\u2014';
    }
    const submitted = new Date(submittedAt).getTime();
    if (Number.isNaN(submitted)) {
      return '\u2014';
    }
    const minutes = Math.max(0, Math.floor((Date.now() - submitted) / 60_000));
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 48) {
      return `${hours}h`;
    }
    return `${Math.floor(hours / 24)}d`;
  }

  statusSeverity(status: MenuItemStatus | null): 'success' | 'warn' | 'danger' | 'info' {
    switch (status) {
      case MenuItemStatus.Approved:
      case MenuItemStatus.Completed:
        return 'success';
      case MenuItemStatus.Rejected:
        return 'danger';
      case MenuItemStatus.PendingApproval:
        return 'warn';
      default:
        return 'info';
    }
  }

  openDetail(item: EntreeV1): void {
    this.router.navigate(['/menu-items', item.id]);
  }

  private async runLoad(query: MenuItemPendingQuery): Promise<void> {
    this.loading.set(true);
    this.error.set(false);

    try {
      const response = await firstValueFrom(this.menuItems.getPendingApprovalList(query));
      this.page.set(response);
      void this.resolveProducerNamesFor(response.data ?? []);
    } catch {
      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  /** Resolve names for just the producers visible on this page; results accumulate in the service cache. */
  private async resolveProducerNamesFor(items: EntreeV1[]): Promise<void> {
    const known = this.producers.producerNameById();
    const missing = [...new Set(items.map((item) => item.producerId))].filter(
      (id) => !known[id] && !this.unresolvedProducerIds().has(id)
    );
    if (missing.length === 0) {
      return;
    }

    const results = await Promise.allSettled(
      missing.map((id) => firstValueFrom(this.producers.getProducerDetail(id)))
    );
    const failed = missing.filter((_, index) => results[index].status === 'rejected');
    if (failed.length > 0) {
      this.unresolvedProducerIds.update((current) => new Set([...current, ...failed]));
    }
  }

  private async loadStatusCounts(): Promise<void> {
    try {
      const counts = await firstValueFrom(this.menuItems.getStatusCounts());
      this.statusCounts.set(Object.fromEntries(counts.map((c) => [c.status, c.count])));
    } catch {
      // Tabs degrade to plain labels without counts.
    }
  }
}
